import React from 'react';
import { useSelector } from 'react-redux';
import { ActionButton, Flex, Slider, View } from '@adobe/react-spectrum';
import { MathUtils, Point, Rectangle } from '@printu/geom';
import { getViewportFitScale, ViewportActionCreators } from '@printu/viewport';
import Maximize from '@spectrum-icons/workflow/Maximize';
import Minimize from '@spectrum-icons/workflow/Minimize';
import { getProjectState } from 'app/stores/project';
import { getPageRect } from 'app/stores/project/page';
import { getCurrentPage, getPageImage } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { Project } from 'app/views/components/editor/project/Project';
import { useScreenRect } from 'app/views/components/footer/minimap/hooks/useScreenRect';
import { MinimapFrame } from 'app/views/components/footer/minimap/MinimapFrame';
import { SVG } from 'app/views/components/svg/SVG';
import { useMouseDrag } from 'app/views/hooks/useMouseDrag';
import { noop } from 'app/utils/function';
const Const = {
    emptyRect: Rectangle.EMPTY.toObject(),
    maxDimention: 240,
};
export const Minimap = () => {
    const dispatch = useAppDispatch();
    const containerRef = React.useRef(null);
    const page = useSelector((state) => getCurrentPage(state));
    const image = useSelector((state) => getPageImage(state, getProjectState(state).activePage));
    const zoom = useSelector((state) => state.viewport.zoom);
    const scaleToFit = useSelector((state) => getViewportFitScale(state.viewport));
    const canMaximize = scaleToFit < 1;
    const sizes = React.useMemo(() => {
        if (!page) {
            return null;
        }
        const pageRect = getPageRect(page, image);
        const size = MathUtils.getSizeFitting(pageRect, {
            width: Const.maxDimention,
            height: Const.maxDimention,
        });
        return {
            map: size.toObject(),
            content: {
                contentWidth: pageRect.width,
                contentHeight: pageRect.height,
            },
        };
    }, [page, image]);
    const [screenRect] = useScreenRect(sizes.map.width, sizes.map.height, sizes.content.contentWidth, sizes.content.contentHeight);
    const screenToMapScale = React.useMemo(() => {
        const miniMapScale = sizes.content.contentWidth / sizes.map.width;
        return miniMapScale * zoom;
    }, [zoom, sizes]);
    const lastDragDelta = React.useRef(new Point());
    const onDragStart = React.useCallback((point) => {
        const $screenRect = new Rectangle(screenRect);
        // center if mouse pressed outside of screen view
        if (!$screenRect.containsPoint(point)) {
            dispatch(ViewportActionCreators.move({
                x: (point.x - $screenRect.center.x) * screenToMapScale,
                y: (point.y - $screenRect.center.y) * screenToMapScale,
            }));
        }
        lastDragDelta.current.setTo(0, 0);
    }, [screenRect, screenToMapScale, dispatch]);
    const onDrag = React.useCallback((point, downPoint) => {
        const lDelta = lastDragDelta.current;
        const mouseDelta = point.subtract(downPoint);
        const dx = mouseDelta.x * screenToMapScale;
        const dy = mouseDelta.y * screenToMapScale;
        const delta = {
            x: -lDelta.x + dx,
            y: -lDelta.y + dy,
        };
        dispatch(ViewportActionCreators.move(delta));
        lDelta.setTo(dx, dy);
        return true;
    }, [screenToMapScale, dispatch]);
    const onMinimize = React.useCallback(() => {
        dispatch(ViewportActionCreators.setMode('fit'));
    }, [dispatch]);
    const onMaximize = React.useCallback(() => {
        dispatch(ViewportActionCreators.setZoom(1));
    }, [dispatch]);
    const onZoomChange = React.useCallback((value) => {
        dispatch(ViewportActionCreators.setZoom(value));
    }, [dispatch]);
    useMouseDrag(containerRef, {
        onDragStart,
        onDragEnd: noop,
        onDrag,
    });
    if (!sizes) {
        return null;
    }
    return (React.createElement(View, { paddingTop: "size-100", paddingX: "size-10", paddingBottom: "size-10" },
        React.createElement(Flex, { direction: "column", gap: "size-20", alignItems: "center" },
            React.createElement("div", { "data-test-id": "zoom-view", ref: containerRef, style: {
                    width: `${sizes.map.width}px`,
                    height: `${sizes.map.height}px`,
                } },
                React.createElement(SVG, { width: "100%", height: "100%", preserveAspectRatio: "none" },
                    React.createElement(Project, null),
                    React.createElement(MinimapFrame, Object.assign({}, sizes.map, sizes.content)))),
            React.createElement(Flex, { direction: "row", width: "90%", gap: "size-10" },
                React.createElement(Slider, { "data-test-id": "zoom-slider", "aria-label": "Zoom", minValue: scaleToFit, maxValue: 8, step: 0.01, value: zoom, flex: 1, onChange: onZoomChange }),
                React.createElement(ActionButton, { "data-test-id": "zoom-min", isQuiet: true, onPress: onMinimize },
                    React.createElement(Minimize, { size: "XS" })),
                canMaximize && (React.createElement(ActionButton, { "data-test-id": "scheme-max", isQuiet: true, onPress: onMaximize },
                    React.createElement(Maximize, { size: "XS" })))))));
};
